import React, { useState, useEffect, useCallback } from 'react'
import { Radio, Button, Tabs, Spin, Popconfirm, Tooltip, Modal } from 'antd'
import {
  updateMedia,
  removeMedias
} from '../../../../../../../services/api/library'
import './certificate.scss'

import template from '../../../../../../../assets/template.svg'
import icon from '../../../../../../../assets/icon.svg'
import logo from '../../../../../../../assets/logo.svg'
import pencil from '../../../../../../../assets/pencil.svg'
import bin from '../../../../../../../assets/bin.svg'
import signature from '../../../../../../../assets/signature.svg'
import Template from '../../../../../../../components/CertificateTemplate'

import { LoadingOutlined } from '@ant-design/icons'
import html2canvas from 'html2canvas'
import { withTranslation } from 'react-i18next'

const { TabPane } = Tabs
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />

const TYPES = {
  ICON: 'Icon',
  SIGNATURE: 'Signature',
  SIGNATURE_TWO: 'SignatureTwo',
  LOGO: 'Logo',
  LOGO_TWO: 'LogoTwo',
  CERTIFICATE: 'Cert'
}

const Certificate = ({
  type,
  getLibraryList,
  upload,
  onUploadImage,
  t,
  user
}) => {
  const userOrg = user?.info?.organization?.title || 'XYZ Organization'

  const TEXT = {
    line1: 'CERTIFICATE',
    line2: 'OF ACHIEVEMENT',
    line3: 'THIS CERTIFICATE IS PRESENTED TO :',
    line4: 'Name Here',
    line5: `The certificate is issued by the ${userOrg} on the basis that the awardee has completed a program of intensive learning that endows them with a fundamental understanding of education. The awardee has the rudimentary knowledge needed to engage in educational activities on a professionally informed basis in the workplace`,
    line6: 'Name Here',
    line7: 'CEO, XYZ Organization',
    line8: 'Name of Person',
    line9: 'Post'
  }

  const [state, setState] = useState({
    icons: [],
    templates: [],
    userIcons: [],
    userLogos: [],
    userCerts: [],
    signatures: [],
    logos: [],
    userSignatures: [],
    currentIndex: 0,
    currentLogoIndex: 0,
    isIconUploading: false,
    isLogoUploading: false,
    isSignatureUploading: false,
    isCertificateLoading: true,
    isCertificateUploading: false,
    isDeleting: false,
    clearCertificate: false,
    addSignature: false,
    certificatesWithHistory: [],
    selectedTemplate: null,
    selectedIcon: null,
    selectedUserIcon: null,
    selectedUserIconId: null,
    selectedUserLogo: null,
    selectedUserLogoId: null,
    certificateTitle: '',
    selectedCertId: null,
    certificateHistory: null,
    toUpdateCertificateId: null,
    isCertificateSaving: false,
    isAllTypeLoading: true,
    pendingImageFetches: 0,
    line1: TEXT.line1,
    line2: '',
    line3: '',
    line4: '',
    line5: TEXT.line5,
    line6: TEXT.line6,
    line7: TEXT.line7,
    line8: '',
    line9: ''
  })

  const [titleError, setTitleError] = useState(false)

  const updateState = useCallback(newState => {
    setState(prev => ({ ...prev, ...newState }))
  }, [])

  const importAll = useCallback(r => {
    let images = {}
    r.keys().forEach(item => {
      images[item.replace('./', '')] = r(item)
    })
    return images
  }, [])

  const importedArray = useCallback(o => {
    let obj = Object.keys(o)
      .map((key, index) => ({ [index]: o[key] }))
      .reduce((a, b) => Object.assign({}, a, b))
    return obj
  }, [])

  const getIconsAndTemplates = useCallback(() => {
    const iconsObj = importAll(
      require.context(
        '../../../../../../../assets/userIcons/',
        false,
        /\.(png|jpe?g|svg)$/
      )
    )
    const templatesObj = importAll(
      require.context(
        '../../../../../../../assets/certificateTemplates/',
        false,
        /\.(png|jpe?g|svg)$/
      )
    )
    const icons = importedArray(iconsObj)
    const templates = importedArray(templatesObj)

    updateState({
      icons,
      templates,
      selectedTemplate: templates[0]
    })
  }, [importAll, importedArray, updateState])

  const getUserImages = useCallback(
    imageType => {
      getLibraryList({
        offset: 0,
        limit: null,
        type: imageType.toUpperCase(),
        sort: 'NEWEST'
      }).then(res => {
        const updates = {
          [`user${imageType}s`]: res.rows,
          [`is${imageType}Uploading`]: false
        }

        updateState(updates)

        if (imageType === TYPES.CERTIFICATE) {
          handleCertificateData(res.rows)
        }

        // decrement global pending fetches and update global loading flag
        setState(prev => {
          const remaining = (prev.pendingImageFetches || 0) - 1
          return {
            ...prev,
            pendingImageFetches: remaining,
            isAllTypeLoading: remaining > 0
          }
        })
      })
    },
    [getLibraryList, updateState]
  )

  const handleCertificateData = useCallback(
    userCerts => {
      const certificatesWithHistory = userCerts.filter(
        item =>
          item.custom_data &&
          Object.keys(item.custom_data).length > 0 &&
          item.custom_data.saveHistory === true
      )

      updateState({
        isDeleting: false,
        isCertificateUploading: false,
        isCertificateLoading: false,
        certificatesWithHistory
      })
    },
    [updateState]
  )

  useEffect(() => {
    getIconsAndTemplates()

    const types = {
      ICON: 'Icon',
      SIGNATURE: 'Signature',
      LOGO: 'Logo',
      CERTIFICATE: 'Cert'
    }

    // initialize global loading state for all image fetches
    setState(prev => ({
      ...prev,
      pendingImageFetches: Object.keys(types).length,
      isAllTypeLoading: true
    }))

    Object.keys(types).forEach(key => {
      const imageType = types[key]
      getUserImages(imageType)
    })
  }, [getIconsAndTemplates, getUserImages])

  const createTabIcon = useCallback(
    (src, text) => (
      <div className="tab_icon">
        <span className="img-template">
          <img alt="" src={src} />
        </span>
        <span>{text}</span>
      </div>
    ),
    []
  )

  const templates = useCallback(
    () => createTabIcon(template, t('v4:templates')),
    [createTabIcon, t]
  )
  const iconTab = useCallback(() => createTabIcon(icon, t('v4:icon')), [
    createTabIcon,
    t
  ])
  const signatureTab = useCallback(
    () => createTabIcon(signature, t('v4:sign')),
    [createTabIcon, t]
  )
  const logoTab = useCallback(() => createTabIcon(logo, t('v4:logo')), [
    createTabIcon,
    t
  ])

  const onIconChange = useCallback(
    e => {
      updateState({
        selectedUserIcon: null,
        selectedUserIconId: null,
        selectedIcon: state.icons[e.target.value]
      })
    },
    [state.icons, updateState]
  )

  const onTemplateChange = useCallback(
    e => {
      updateState({ selectedTemplate: state.templates[e.target.value] })
    },
    [state.templates, updateState]
  )

  const onUserImageChange = useCallback(
    (item, imageType) => {
      const updates = {
        [`selectedUser${imageType}`]: item.link,
        [`selectedUser${imageType}Id`]: item.id
      }

      if (imageType === 'Icon') {
        updates.selectedIcon = null
      }

      updateState(updates)
    },
    [updateState]
  )

  const handleUserUpload = useCallback((e, imageType) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      uploadUserImage(reader.result, file, imageType)
    }
    reader.readAsDataURL(file)
  }, [])

  const uploadUserImage = useCallback(
    (image, data, imageType) => {
      updateState({
        [`userUploaded${imageType}`]: image,
        [`is${imageType}Uploading`]: true
      })

      const body = {
        croppedImage: image,
        lastModifiedDate: new Date().getTime(),
        name: data.name,
        type: data.type,
        size: data.size,
        imgFormat: imageType.toUpperCase()
      }

      upload(body).then(res => {
        updateState({
          [`userUploaded${imageType}Id`]: res.id,
          [`is${imageType}Uploading`]: false
        })
        getUserImages(imageType)
      })
    },
    [upload, updateState, getUserImages]
  )

  const handleCertificateText = useCallback(
    item => {
      updateState({
        [item.name]: item.value,
        certificateHistory: null
      })
    },
    [updateState]
  )

  const getCustomTextData = useCallback(() => {
    const {
      line1,
      line2,
      line3,
      line4,
      line5,
      line6,
      line7,
      line8,
      line9
    } = state
    const textValues = {
      line1: line1 || TEXT.line1,
      line2: line2 || TEXT.line2,
      line3: line3 || TEXT.line3,
      line4: line4 || TEXT.line4,
      line5: line5 || TEXT.line5,
      line6: line6 || TEXT.line6,
      line7: line7 || TEXT.line7,
      line8: line8 || TEXT.line8,
      line9: line9 || TEXT.line9
    }

    return Object.keys(textValues)
      .filter(key => textValues[key] !== undefined)
      .reduce((obj, key) => {
        obj[key] = textValues[key]
        return obj
      }, {})
  }, [state])

  const handleImageInfo = useCallback(async () => {
    window.scrollTo(0, 0)
    const certificate = document.getElementById('certificate')
    const customTextData = getCustomTextData()

    const canvas = await html2canvas(certificate)
    const image = canvas.toDataURL('image/png', canvas.width, canvas.height)
    const y = image.slice(-2) === '==' ? 2 : 1
    const x = image.length * (3 / 4) - y

    const logosById = getImageById(state.logos, 'LOGOS')

    return {
      croppedImage: image,
      lastModifiedDate: new Date(),
      [state.toUpdateCertificateId
        ? 'filename'
        : 'name']: state.certificateTitle,
      size: x,
      type: 'image/png',
      imgFormat: 'CERT',
      customData: {
        ...customTextData,
        logos: logosById,
        selectedUserIconId: state.selectedUserIconId,
        selectedIcon: state.selectedIcon,
        selectedUserLogoId: state.selectedUserLogoId,
        signatures: state.signatures,
        selectedTemplate: state.selectedTemplate,
        saveHistory: true
      }
    }
  }, [getCustomTextData, state])

  const saveUserCertificate = useCallback(async () => {
    updateState({ isCertificateUploading: true })

    try {
      const imageData = await handleImageInfo()

      if (state.toUpdateCertificateId) {
        await updateMedia(state.toUpdateCertificateId, imageData)
        updateState({ toUpdateCertificateId: null })
      } else {
        await upload(imageData)
      }

      getUserImages(TYPES.CERTIFICATE)
    } catch (error) {
      console.error('Error saving certificate:', error)
    }
  }, [
    handleImageInfo,
    state.toUpdateCertificateId,
    upload,
    updateState,
    getUserImages
  ])

  const clearCertificate = useCallback(() => {
    updateState({
      selectedTemplate: state.templates[0],
      certificateTitle: '',
      selectedIcon: null,
      selectedUserIcon: null,
      signatures: [],
      logos: [],
      selectedUserLogo: null,
      selectedUserIconId: null,
      selectedUserLogoId: null,
      addSignature: false,
      certificateHistory: null,
      toUpdateCertificateId: null,
      clearCertificate: true,
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
      line6: '',
      line7: '',
      line8: '',
      line9: ''
    })
  }, [state.templates, updateState])

  const onClearImage = useCallback(
    imageType => {
      switch (imageType) {
        case TYPES.ICON:
          updateState({
            selectedIcon: null,
            selectedUserIcon: null,
            selectedUserIconId: null
          })
          break
        case TYPES.LOGO:
          updateState({
            currentLogoIndex: 1,
            logos: [state.logos[1]]
          })

          break
        case TYPES.LOGO_TWO:
          updateState({
            currentLogoIndex: 1,
            logos: [state.logos[0]]
          })
          break
        case TYPES.SIGNATURE:
          if (state.addSignature) {
            updateState({
              addSignature: false,
              currentIndex: 1,
              signatures: [state.signatures[1]]
            })
          } else {
            updateState({
              addSignature: false,
              currentIndex: 0,
              signatures: []
            })
          }
          break
        case TYPES.SIGNATURE_TWO:
          updateState({
            addSignature: false,
            currentIndex: 1,
            signatures: [state.signatures[0]]
          })
          break
        default:
          break
      }
    },
    [state.addSignature, state.signatures, state.logos, updateState]
  )

  const onClear = useCallback(() => {
    updateState({ clearCertificate: false })
  }, [updateState])

  const addSignature = useCallback(() => {
    updateState({ addSignature: true })
  }, [updateState])

  const onUserSignatureSelect = useCallback(
    item => {
      const { signatures, currentIndex, addSignature } = state
      const mySigns = [...signatures]
      const index = addSignature ? currentIndex : 0
      const data = { id: item.id, link: item.link }
      mySigns[index] = data
      const nextIndex = (index + 1) % 2

      updateState({
        signatures: mySigns,
        currentIndex: nextIndex
      })
    },
    [state, updateState]
  )

  const onUserLogoSelect = useCallback(
    item => {
      const { logos, currentLogoIndex } = state
      const myLogos = [...logos]
      const data = { id: item.id, link: item.link }
      myLogos[currentLogoIndex] = data
      const nextIndex = (currentLogoIndex + 1) % 2

      updateState({
        logos: myLogos,
        currentLogoIndex: nextIndex
      })
    },
    [state, updateState]
  )

  const putItemInCanvas = useCallback(
    (item, type) => {
      const { custom_data } = item
      if (custom_data) console.log(custom_data)

      const userIcon =
        custom_data.selectedUserIconId &&
        getImageById(custom_data.selectedUserIconId, TYPES.ICON)

      updateState({
        line1: custom_data.line1 || state.line1,
        line2: custom_data.line2 || state.line2,
        line3: custom_data.line3 || state.line3,
        line4: custom_data.line4 || state.line4,
        line5: custom_data.line5 || state.line5,
        line6: custom_data.line6 || state.line6,
        line7: custom_data.line7 || state.line7,
        line8: custom_data.line8 || state.line8,
        line9: custom_data.line9 || state.line9,
        selectedIcon: custom_data.selectedIcon || null,
        selectedUserIcon: userIcon || null,
        selectedUserIconId: custom_data.selectedUserIconId || null,
        selectedUserLogo: custom_data.selectedUserLogo || null,
        selectedUserLogoId: custom_data.selectedUserLogoId || null,
        addSignature: custom_data.signatures.length === 2,
        signatures: custom_data.signatures || [],
        logos: custom_data.logos || [],
        selectedTemplate: custom_data.selectedTemplate || null,
        certificateHistory: item.custom_data,
        certificateTitle: type === 'edit' ? item.filename : ''
      })
    },
    [state, updateState]
  )

  const editCertificate = useCallback(
    (item, type) => {
      updateState({
        toUpdateCertificateId: type === 'edit' ? item.id : null,
        selectedCertId: type === 'edit' ? item.id : null
      })
      putItemInCanvas(item, type)
    },
    [updateState, putItemInCanvas]
  )

  const deleteCertificateHistory = useCallback(
    item => {
      const data = {
        mediaIds: [item.id],
        custom_data: {
          ...item.custom_data,
          saveHistory: false
        }
      }

      updateState({ isDeleting: true, toUpdateCertificateId: item.id })

      removeMedias(data)
        .then(() => {
          updateState({
            toUpdateCertificateId: null,
            isDeleting: false,
            certificatesWithHistory: state.certificatesWithHistory.filter(
              cert => cert.id !== item.id
            )
          })
        })
        .catch(error => {
          console.error('Error deleting certificate:', error)
          updateState({ isDeleting: false })
        })
    },
    [state.certificatesWithHistory, updateState]
  )

  const uploadCertificate = useCallback(async () => {
    updateState({ isCertificateSaving: true })

    try {
      const body = await handleImageInfo()
      await onUploadImage(body)
    } catch (error) {
      console.error('Error uploading certificate:', error)
    }
  }, [handleImageInfo, onUploadImage, updateState])

  const getImageById = useCallback(
    (params, imageType) => {
      if (!params) return null

      if (imageType === TYPES.LOGO) {
        const logos = state.userLogos || []
        const data = logos.find(logo => String(logo.id) === String(params))
        return data && data.link
      } else if (imageType === TYPES.ICON) {
        const icons = state.userIcons || []
        const data = icons.find(icon => String(icon.id) === String(params))
        return data && data.link
      } else if (imageType === TYPES.SIGNATURE) {
        return params.map(item => {
          const signature = state.userSignatures.find(
            signature => signature.id === item.id
          )
          return signature ? { id: signature.id, link: signature.link } : item
        })
      } else if (imageType === 'LOGOS') {
        const logos = state.userLogos || []
        return params.map(item => {
          const itemId = item && item.id
          const logo = logos.find(logo => String(logo.id) === String(itemId))
          return logo ? { id: logo.id, link: logo.link } : item
        })
      }
    },
    [state.userLogos, state.userIcons, state.userSignatures]
  )

  const handleCertName = useCallback(
    e => {
      const inputValue = e.target.value
      const maxLength = 150
      setTitleError(false)
      updateState({
        certificateTitle:
          inputValue.length <= maxLength
            ? inputValue
            : inputValue.slice(0, maxLength)
      })
    },
    [updateState]
  )

  const iconById = getImageById(state.selectedUserIconId, TYPES.ICON)
  const logoById = getImageById(state.selectedUserLogoId, TYPES.LOGO)
  const signaturesById = getImageById(state.signatures, TYPES.SIGNATURE)
  const logosById = getImageById(state.logos, 'LOGOS')

  console.log({
    iconById,
    logoById,
    signaturesById,
    logosById,
    selectedIcon: state
  })

  // Modal.confirm handlers for each button

  const handleDiscard = () => {
    Modal.confirm({
      title: t('v4:discard_changes'),
      content:
        'Are you sure you want to discard your changes? The changes will be lost and you will start over from scratch.', // t('v4:are_you_sure_discard'),
      centered: true,
      onOk: () => clearCertificate(),
      okText: t('v4:ok'),
      cancelText: t('v4:cancel')
    })
  }

  const handleSave = () => {
    if (!state.certificateTitle || state.certificateTitle.trim() === '') {
      setTitleError(true)
      return
    }
    Modal.confirm({
      title: t('v4:certificate_save_btn') + type,
      content: t('v5:are_you_sure_you_want_to_attach_this_certificate'),
      centered: true,
      onOk: () => uploadCertificate(),
      okText: t('v4:ok'),
      cancelText: t('v4:cancel')
    })
  }

  const handleSaveForLater = () => {
    if (!state.certificateTitle || state.certificateTitle.trim() === '') {
      setTitleError(true)
      return
    }
    Modal.confirm({
      title: state.toUpdateCertificateId
        ? 'Update Certificate'
        : t('v3:ai_save_later'),
      content: t('v5:are_you_sure_you_want_to_save_this_certificate_for_later'),
      centered: true,
      okText: t('general:save'),
      cancelText: t('v4:cancel'),
      onOk: () => saveUserCertificate()
    })
  }

  return (
    <React.Fragment>
      <div className="certificate_modal_wrap">
        <h1 className="certificate_modal_title">
          {state.toUpdateCertificateId
            ? t('v4:edit_certificate')
            : t('v4:create_certificate')}
        </h1>
        <div className="certificate_modal_content">
          <div className="certificate_create_left">
            <div className="certificate_container">
              <div className="left_panel">
                <div className="tabs_panel">
                  <Tabs className="tabs" defaultActiveKey="1" tabPosition="top">
                    <TabPane className="tab" tab={templates()} key="1">
                      <Radio.Group
                        className="certs-container certificate"
                        onChange={onTemplateChange}
                        defaultValue="0"
                      >
                        {Object.entries(state.templates).map(([key, val]) => (
                          <Radio.Button
                            className="certificate__icon"
                            value={key}
                            key={key}
                          >
                            <div>
                              <img alt="" src={val} />
                            </div>
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </TabPane>

                    <TabPane className="tab" tab={iconTab()} key="3">
                      <Radio.Group
                        className="certs-container icon"
                        onChange={onIconChange}
                        value={state.selectedIcon}
                      >
                        {Object.entries(state.icons).map(([key, val]) => (
                          <Radio.Button
                            className="certificate__icon"
                            value={key}
                            key={key}
                          >
                            <img alt="" src={val} />
                          </Radio.Button>
                        ))}
                      </Radio.Group>

                      {state.userIcons && (
                        <div className="user_icon">
                          {state.userIcons.map(item => (
                            <div
                              onClick={() =>
                                onUserImageChange(item, TYPES.ICON)
                              }
                              className="certificate__icon"
                              key={item.id}
                            >
                              <img alt="" src={item.link} />
                            </div>
                          ))}
                        </div>
                      )}

                      <label
                        className="btn-upload"
                        style={{ pointerEvents: 'all' }}
                      >
                        <input
                          type="file"
                          name="fileupload"
                          onChange={e => handleUserUpload(e, TYPES.ICON)}
                        />
                        <button className="btn">
                          {state.isIconUploading && (
                            <Spin indicator={antIcon} />
                          )}
                          {t('v4:upload_icon')}
                        </button>
                      </label>
                    </TabPane>

                    <TabPane className="tab" tab={signatureTab()} key="4">
                      {state.userSignatures && (
                        <div className="user_icon">
                          {state.userSignatures.map(item => (
                            <div
                              onClick={() => onUserSignatureSelect(item)}
                              className="certificate__icon"
                              key={item.id}
                            >
                              <img alt="" src={item.link} />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="signature-upload-wrap">
                        <div>
                          <label
                            className="btn-upload"
                            style={{ pointerEvents: 'all' }}
                          >
                            <input
                              type="file"
                              name="fileupload"
                              onChange={e =>
                                handleUserUpload(e, TYPES.SIGNATURE)
                              }
                            />
                            <button className="btn">
                              {state.isSignatureUploading && (
                                <Spin indicator={antIcon} />
                              )}
                              {t('v4:upload_signature')}
                            </button>
                          </label>
                        </div>
                        {/* {!state.addSignature && state.signatures.length === 1 && (
                          <Button onClick={addSignature}>
                            {t('v4:add_another_sign')}
                          </Button>
                        )} */}
                      </div>
                    </TabPane>

                    <TabPane className="tab" tab={logoTab()} key="5">
                      {state.userLogos && (
                        <div className="user_icon">
                          {state.userLogos.map(item => (
                            <div
                              onClick={() => onUserLogoSelect(item)}
                              className="certificate__icon"
                              key={item.id}
                            >
                              <img alt="" src={item.link} />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="logo-upload-wrap">
                        <div>
                          <label
                            className="btn-upload"
                            style={{ pointerEvents: 'all' }}
                          >
                            <input
                              type="file"
                              name="fileupload"
                              onChange={e => handleUserUpload(e, TYPES.LOGO)}
                            />
                            <button className="btn">
                              {state.isLogoUploading && (
                                <Spin indicator={antIcon} />
                              )}
                              {t('v4:upload_logo')}
                            </button>
                          </label>
                        </div>
                      </div>
                    </TabPane>
                  </Tabs>
                </div>

                <div className="canvas" id="certificate">
                  <Template
                    icon={state.selectedIcon}
                    userIcon={state.selectedUserIcon}
                    iconById={iconById}
                    logo={state.selectedUserLogo}
                    logoById={logoById}
                    signatures={state.signatures}
                    signaturesById={signaturesById}
                    logos={state.logos}
                    logosById={logosById}
                    onText={handleCertificateText}
                    certificateHistory={state.certificateHistory}
                    template={state.selectedTemplate}
                    clearCertificate={state.clearCertificate}
                    onClear={onClear}
                    addSignature={state.addSignature}
                    onClearImage={onClearImage}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="certificate_create_right">
            <div className="certificate_history">
              <div className="title">{t('v4:certificate_history')}</div>
              <div className="history_items">
                <Spin
                  indicator={antIcon}
                  spinning={state.isCertificateLoading}
                />
                {state.certificatesWithHistory.map(item => (
                  <Spin
                    key={item.id}
                    spinning={
                      item.id === state.toUpdateCertificateId &&
                      state.isDeleting
                    }
                    tip="Deleting..."
                  >
                    <div
                      className={`history_item ${
                        state.selectedCertId === item.id ? ' active' : ''
                      }`}
                      onClick={
                        state.isAllTypeLoading
                          ? undefined
                          : () => editCertificate(item, 'view')
                      }
                    >
                      <div className="history_item_details">
                        <img
                          alt=""
                          src={item.custom_data.selectedTemplate}
                          onError={v => (v.target.src = null)}
                        />
                        <div className="history_item_info">
                          <Tooltip title={item.filename}>
                            <h3>{item.filename}</h3>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="history_item_action">
                        <div
                          className="history_item_action_img"
                          onClick={
                            state.isAllTypeLoading
                              ? undefined
                              : e => {
                                  e.stopPropagation()
                                  editCertificate(item, 'edit')
                                }
                          }
                          style={{
                            pointerEvents: state.isAllTypeLoading
                              ? 'none'
                              : 'auto',
                            opacity: state.isAllTypeLoading ? 0.5 : 1
                          }}
                        >
                          <img alt="" src={pencil} />
                        </div>
                        <Popconfirm
                          title={t('v4:do_you_want_delete_cert')}
                          onConfirm={() => deleteCertificateHistory(item)}
                          okText="Yes"
                          cancelText="No"
                          icon={null}
                        >
                          <div className="history_item_action_img">
                            <img alt="" src={bin} />
                          </div>
                        </Popconfirm>
                      </div>
                    </div>
                  </Spin>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="detail">
          <div
            className={`title d-col${titleError ? ' cert-title-error' : ''}`}
          >
            <div className="cert-item-title">
              <span>{t('v4:name_of_cert')}</span>
              <span className="max-character">{t('v4:should_less_150')}</span>
            </div>
            <input
              type="text"
              value={state.certificateTitle}
              onChange={handleCertName}
              name="certificateTitle"
              className={titleError ? 'error' : ''}
            />
            {titleError && (
              <div style={{ color: '#ff4d4f', marginTop: 4, fontSize: 13 }}>
                {t('v4:certificate_title_required') ||
                  t('v5:certificate_title_is_required')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="certificate_buttons">
        <div className="actions_left">
          <Button type="link" danger size="middle" onClick={handleDiscard}>
            {t('v4:discard_changes')}
          </Button>
        </div>
        <div className="actions_right">
          <Button
            size="middle"
            loading={state.isCertificateUploading}
            onClick={handleSaveForLater}
          >
            {state.toUpdateCertificateId
              ? t('v4:upload_cert')
              : t('v3:ai_save_later')}
          </Button>
          <Button
            type="primary"
            size="middle"
            onClick={handleSave}
            loading={state.isCertificateSaving}
          >
            {t('v4:certificate_save_btn') + type}
          </Button>
        </div>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(Certificate)
